/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Suspense } from 'react'
import {
  createBrowserRouter,
  Location,
  Navigate,
  useParams,
} from 'react-router-dom'
import UserLayout from 'src/layouts/UserLayout'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import {
  usePermission,
  PermissionPath,
  useSchemaPermissions,
} from 'src/hooks/usePermission'

const pagesCache = new Map<string, React.ComponentType<any>>()

const load = (props: { page: string; permission?: PermissionPath }) => () => {
  const { permission } = props
  const isAccess = usePermission(permission)
  const page = isAccess ? props.page : 'access-denied'

  const PageCache = pagesCache.get(page)

  if (PageCache) {
    return <PageCache />
  } else {
    const Page = React.lazy(() =>
      import(`src/pages/${page}`).then((pageModule) => {
        pagesCache.set(page, pageModule.default)

        return pageModule
      })
    )

    return (
      <Suspense
        fallback={
          <Stack
            sx={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <Page />
      </Suspense>
    )
  }
}

const loadStoragePage =
  (props: { page: string; permission?: PermissionPath }) => () => {
    const { permission } = props
    const isAccess = usePermission(permission)
    const { schemeName = '' } = useParams<{ schemeName: string }>()
    const schemaPermissions = useSchemaPermissions(schemeName)

    const page =
      isAccess && schemaPermissions.show ? props.page : 'access-denied'

    let PageCache = pagesCache.get(page)

    if (!PageCache) {
      PageCache = React.lazy(() =>
        import(`src/pages/${page}`).then((pageModule) => {
          return pageModule
        })
      )

      pagesCache.set(page, PageCache)
    }

    return (
      <Suspense
        fallback={
          <Stack
            sx={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <PageCache />
      </Suspense>
    )
  }

const AdminFieldsRedirect = () => {
  const { schemeName = '' } = useParams<{ schemeName: string }>()

  return (
    <Navigate to={`/settings/project/objects/${schemeName}/fields`} replace />
  )
}

const AdminGroupsRedirect = () => {
  const { schemeName = '' } = useParams<{ schemeName: string }>()

  return (
    <Navigate to={`/settings/project/objects/${schemeName}/fields`} replace />
  )
}

export const router = createBrowserRouter([
  {
    element: <UserLayout contentHeightFixed />,
    children: [
      {
        path: '',
        Component: load({
          page: 'dashboard',
          permission: undefined,
        }),
      },

      {
        path: 'callHistory',
        Component: load({
          page: 'callHistory',
          permission: undefined,
        }),
      },
      {
        path: 'marketing',
        Component: load({
          page: 'marketing',
          permission: undefined,
        }),
      },
      {
        path: 'design-system',
        Component: load({
          page: 'design-system',
          permission: undefined,
        }),
      },
      {
        path: 'profile',
        element: <Navigate to="/settings/personal/profile" replace />,
      },

      {
        path: 'settings',
        Component: load({
          page: 'settings',
        }),
        children: [
          {
            index: true,
            element: <Navigate to="/settings/project/general" replace />,
          },
          {
            path: 'project',
            element: <Navigate to="/settings/project/general" replace />,
          },
          {
            path: 'project/general',
            Component: load({
              page: 'settings/project/general',
            }),
          },
          {
            path: 'project/members/users',
            Component: load({
              page: 'admin/users',
              permission: 'auth.users',
            }),
          },
          {
            path: 'project/members/groups',
            Component: load({
              page: 'admin/users-groups',
              permission: 'auth.groups',
            }),
          },
          {
            path: 'project/objects',
            Component: load({
              page: 'admin/schemes',
              permission: 'storage.objectScheme',
            }),
          },
          {
            path: 'project/objects/map',
            Component: load({
              page: 'settings/project/objects/map',
              permission: 'storage.objectScheme',
            }),
          },
          {
            path: 'project/objects/:schemeName/fields',
            Component: load({
              page: 'admin/fields/schemeName',
              permission: 'storage.objectScheme',
            }),
          },
          {
            path: 'project/objects/:schemeName/groups',
            Component: AdminGroupsRedirect,
          },
          {
            path: 'project/communication/numbers',
            Component: load({
              page: 'admin/numbers',
              // permission: 'communication.twilio.index',
            }),
          },
          {
            path: 'project/communication/telegram',
            Component: load({
              page: 'admin/tgNumbers',
              // permission: 'communication.telegram.index',
            }),
          },
          {
            path: 'project/communication/auto-creation',
            Component: load({
              page: 'settings/project/general',
            }),
          },
          {
            path: 'project/integrations/apps',
            Component: load({
              page: 'admin/integration',
            }),
          },
          {
            path: 'project/integrations/api-keys',
            Component: load({
              page: 'admin/api-keys',
              permission: 'auth.users',
            }),
          },
          {
            path: 'project/billing',
            Component: load({
              page: 'billing-history',
              permission: 'auth.partner.billingStat',
            }),
          },
          {
            path: 'personal',
            element: <Navigate to="/settings/personal/profile" replace />,
          },
          {
            path: 'personal/profile',
            Component: load({
              page: 'profile',
            }),
          },
          {
            path: 'personal/mail',
            Component: load({
              page: 'settings/personal/mail',
            }),
          },
        ],
      },

      {
        path: 'storage/:schemeName',
        Component: load({
          page: 'objectContainer',
        }),
        children: [
          {
            path: '',
            Component: loadStoragePage({
              page: 'storage/object',
            }),
          },
          {
            path: ':objectId',
            Component: loadStoragePage({
              page: 'storage/object/id',
            }),
          },
          {
            path: ':objectId/details',
            Component: loadStoragePage({
              page: 'storage/object/id/details',
            }),
            children: [
              {
                path: '',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/dashboard',
                }),
              },
              {
                path: 'dashboard',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/dashboard',
                }),
              },
              {
                path: 'trends',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/trends',
                }),
              },
              {
                path: 'events',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/events',
                }),
              },
              {
                path: 'elements',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/elements',
                }),
              },
              {
                path: 'contacts',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/contacts',
                }),
              },
              {
                path: 'nps',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/nps',
                }),
              },
              {
                path: 'settings',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/settings',
                }),
              },
              {
                path: 'touch-points',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/touch-points',
                }),
              },
              {
                path: 'tasks',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/tasks',
                }),
              },
              {
                path: 'notes',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/notes',
                }),
              },
              {
                path: 'conversations',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/conversations',
                }),
              },
              {
                path: 'emails',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/emails',
                }),
              },
              {
                path: 'attachments',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/attachments',
                }),
              },
              {
                path: 'play-books',
                Component: loadStoragePage({
                  page: 'storage/object/id/details/play-books',
                }),
              },
            ],
          },
        ],
      },

      // {
      //   path: 'tasks',
      //   Component: load({
      //     page: 'tasks',
      //     permission: 'tasks.index',
      //   }),
      // },
      // {
      //   path: 'health-score',
      //   Component: load({
      //     page: 'healthScore',
      //     permission: undefined,
      //   }),
      // },

      {
        path: 'email',
        Component: load({
          page: 'email',
          // permission: 'mail.thred.index',
        }),
      },
      // {
      //   path: 'playbooks',
      //   Component: load({
      //     page: 'playbooks',
      //     permission: 'playbooks.index',
      //   }),
      // },
      {
        path: 'templates',
        children: [
          {
            path: ':type',
            Component: load({
              page: 'templates',
              // permission: 'templates.index',
            }),
          },
        ],
      },
      {
        path: 'numbers',
        element: (
          <Navigate to="/settings/project/communication/numbers" replace />
        ),
      },
      {
        path: 'billing-history',
        element: <Navigate to="/settings/project/billing" replace />,
      },
      {
        path: 'tg-accounts',
        element: (
          <Navigate to="/settings/project/communication/telegram" replace />
        ),
      },
      {
        path: 'forms',
        Component: load({
          page: 'forms',
          // permission: 'auth.users',
        }),
      },
      {
        path: 'admin',
        children: [
          {
            path: 'fields/:schemeName',
            Component: AdminFieldsRedirect,
          },

          {
            path: 'groups/:schemeName',
            Component: AdminGroupsRedirect,
          },

          {
            path: 'users',
            element: <Navigate to="/settings/project/members/users" replace />,
          },
          {
            path: 'integration',
            element: (
              <Navigate to="/settings/project/integrations/apps" replace />
            ),
          },
          {
            path: 'users-groups',
            element: <Navigate to="/settings/project/members/groups" replace />,
          },
          {
            path: 'api-keys',
            element: (
              <Navigate to="/settings/project/integrations/api-keys" replace />
            ),
          },
          {
            path: 'schemes',
            element: <Navigate to="/settings/project/objects" replace />,
          },
        ],
      },
    ],
  },
  {
    element: <UserLayout />,
    children: [
      {
        path: '*',
        Component: load({
          page: '404',
          permission: undefined,
        }),
      },
    ],
  },
  {
    path: 'login',
    Component: load({
      page: 'login',
      permission: undefined,
    }),
  },
  {
    path: 'callback',
    Component: load({
      page: 'oauth/callback',
      permission: undefined,
    }),
  },
])

export interface RouterHistory {
  locations: Location[]
  getPrevLocation: () => Location | null
}
export const routerHistory: RouterHistory = {
  locations: [],
  getPrevLocation: () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [current, prev] = routerHistory.locations

    return prev
  },
}

router.subscribe(({ location }) => {
  const [prev] = routerHistory.locations

  const isNotEqual =
    prev &&
    // prev.hash !== location.hash ||
    prev.pathname !== location.pathname
  // prev.search !== location.search ||
  // prev.state !== location.state

  if (prev && isNotEqual) {
    routerHistory.locations = [{ ...location }, ...routerHistory.locations]
  }
  if (!prev) {
    routerHistory.locations = [{ ...location }]
  }
  if (routerHistory.locations.length > 5) {
    routerHistory.locations = routerHistory.locations.slice(0, 5)
  }
})

export default router
